import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Link from '@mui/material/Link';
import useSessionContext from '../../data/queryHooks/useSessionContext';
import {
	GetClinicLocations,
	SendPendingAshlinkUserInvitationFn,
} from '../../data/apiCalls';
import LoadingButton from '../shared/LoadingButton';
import {
	getLevel1Text,
	getLevel2Text,
	getLevel3Text,
} from '../../scripts/subAccountLevelText';
import PermissionsModal from '../shared/Modal/PermissionsModal';
import SubAccountClinicsTable from './shared/SubAccountClinicsTable';

const SlideLeft = props => <Slide {...props} direction='left' />;

const CreateSubAccount = () => {
	const [alertText, setAlertText] = useState({ title: '', description: '' });
	const [openSnackbar, setOpenSnackbar] = useState('');
	const [showPermissions, setShowPermissions] = useState(false);

	const {
		ashlinkUserEntityType,
		ashlinkUserId,
		isTest,
		providerId,
		providerNetCode,
		practitionerId,
		practitionerNetCode,
		providerSpecialtyCode,
		providerUserTypeGroup,
	} = useSessionContext();

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// state for locking and selecting all clinics when user level is 1
	const [ashlinkUserLevel, setAshlinkUserLevel] = useState(3);

	// const { values: formValues } = useFormikContext();
	// useEffect(() => {
	// 	setAshlinkUserLevel(formValues.accessLevel);
	// }, [formValues.accessLevel]);

	const { mutate: sendPendingAshlinkUserInvitation, isLoading: isFetching } =
		useMutation(values => SendPendingAshlinkUserInvitationFn(...values), {
			onSuccess: email => {
				queryClient.invalidateQueries('PendingSubAccount');
				sessionStorage.setItem('isCreate', true);
				navigate('/account/manageSubAccounts', {
					state: { email },
				});
			},
			onError: () => {
				const text = {
					title: 'Sorry, something went wrong.',
					description: `We were unable to process your request.`,
				};
				setAlertText(text);
				setOpenSnackbar('error');
			},
		});

	// Available Clinics State
	const [addSelection, setAddSelection] = useState([]);
	const [availableClinicsSearchString, setAvailableClinicsSearchString] =
		useState('');
	const [availableClinicsPage, setAvailableClinicsPage] = useState(0);
	const [availableClinicsPageSize, setAvailableClinicsPageSize] =
		useState(10);

	const { data: availableClinicsData, isLoading: isLoadingAvailableClinics } =
		useQuery({
			queryKey: [
				'ClinicLocations',
				ashlinkUserId,
				providerId,
				ashlinkUserEntityType,
				providerNetCode,
				practitionerId,
				practitionerNetCode,
				availableClinicsSearchString || '',
				availableClinicsPage || 0,
				availableClinicsPageSize || 10,
			],
			queryFn: () =>
				GetClinicLocations(
					ashlinkUserId,
					providerId,
					ashlinkUserEntityType,
					providerNetCode,
					practitionerId,
					practitionerNetCode,
					availableClinicsSearchString,
					availableClinicsPage + 1, // endpoint is 1 indexed, but data grid is 0 indexed
					availableClinicsPageSize ?? 10 // default to 10 if pageSize is not set (this is the data grid default)
				),
			staleTime: Infinity,
			refetchOnWindowFocus: false,
			keepPreviousData: true,
		});

	// Automatically select all clinics when user level is 1
	useEffect(() => {
		if (
			Number(ashlinkUserLevel) === 1 &&
			availableClinicsData?.data?.data?.ashlinkUserLocationByEntity
		) {
			const allClinicIds =
				availableClinicsData.data.data.ashlinkUserLocationByEntity
					.map(
						clinic => clinic.id || clinic.drFedId || clinic.DrFedId
					)
					.filter(Boolean);
			setAddSelection(allClinicIds);
		}
	}, [ashlinkUserLevel, availableClinicsData]);

	const handleClose = reason => {
		if (reason === 'clickaway') {
			return;
		}
		setOpenSnackbar('');
	};

	const validationSchema = Yup.object().shape({
		emailAddress: Yup.string()
			.email('Please enter a valid email address')
			.required('Please enter a valid email address')
			.max(100, 'Email address cannot exceed 100 characters'),
		confirmEmailAddress: Yup.string()
			.oneOf(
				[Yup.ref('emailAddress'), null],
				'Email address does not match'
			)
			.required('Email address does not match'),
		clinics: Yup.array().when('accessLevel', {
			is: 1,
			then: () => Yup.array(),
			otherwise: () =>
				Yup.array().min(1, 'Please select at least one clinic'),
		}),
		accessLevel: Yup.number(),
	});

	const handleSubmit = async values => {
		console.log(
			'Submitting form with values:',
			ashlinkUserLevel === 1 ? [] : addSelection
		);
		await sendPendingAshlinkUserInvitation([
			values.emailAddress,
			values.accessLevel,
			isTest,
			providerId,
			providerSpecialtyCode,
			providerNetCode,
			practitionerId,
			practitionerNetCode,
			ashlinkUserLevel === 1 ? [] : addSelection, // If user level is 1, all clinics will be added automatically in backend
		]);
	};

	return (
		<>
			<Paper elevation={0}>
				<Formik
					initialValues={{
						emailAddress: '',
						confirmEmailAddress: '',
						clinics: [],
						accessLevel: 3,
					}}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({
						setFieldValue,
						values,
						touched,
						errors,
						handleChange,
						handleBlur,
					}) => (
						<Form>
							<Stack sx={{ py: 3.5, px: 4 }}>
								<Typography variant='h5' component='h1'>
									Create New Sub Account
								</Typography>
								<Divider sx={{ my: 2 }} />
								<Typography mb={4.5}>
									Fill out the information below to send an
									invite for your staff to create a new sub
									account.{' '}
								</Typography>
								<TextField
									label='Sub Account Email Address'
									data-testid='email-address'
									value={values.emailAddress}
									name='emailAddress'
									onChange={handleChange}
									onBlur={handleBlur}
									error={
										touched.emailAddress &&
										Boolean(errors.emailAddress)
									}
									helperText={
										touched.emailAddress &&
										errors.emailAddress
									}
									sx={{
										mb: 4.25,
										width: { xs: '100%', md: 440 },
									}}
								/>

								<TextField
									label='Confirm Sub Account Email Address'
									data-testid='confirmEmail-address'
									value={values.confirmEmailAddress}
									name='confirmEmailAddress'
									onChange={handleChange}
									onBlur={handleBlur}
									error={
										touched.confirmEmailAddress &&
										Boolean(errors.confirmEmailAddress)
									}
									helperText={
										touched.confirmEmailAddress &&
										errors.confirmEmailAddress
									}
									sx={{
										mb: 1,
										width: { xs: '100%', md: 440 },
									}}
								/>
								<SubAccountClinicsTable
									pageTitle='Select Clinic(s):'
									clinics={
										availableClinicsData?.data?.data
											?.ashlinkUserProfiles ?? []
									}
									isLoading={isLoadingAvailableClinics}
									selectionModel={
										ashlinkUserLevel === 1
											? []
											: addSelection
									}
									selectionModelUpdate={selections => {
										setFieldValue('clinics', selections);
										setAddSelection(selections);
									}}
									locked={ashlinkUserLevel === 1}
									allChecked={ashlinkUserLevel === 1}
									error={touched.clinics && errors.clinics}
									page={availableClinicsPage}
									pageSize={availableClinicsPageSize}
									onPageChange={newPage =>
										setAvailableClinicsPage(newPage)
									}
									totalRows={
										availableClinicsData?.data?.data
											?.totalRows ?? 0
									}
									onPageSizeChange={newPageSize => {
										setAvailableClinicsPageSize(
											newPageSize
										);
									}}
									onSearchStrChange={newSearchStr =>
										setAvailableClinicsSearchString(
											newSearchStr
										)
									}
								/>

								<Box
									sx={{
										mt: 6,
									}}
								>
									<Typography
										variant='h6'
										component='label'
										id='accessLevel-label'
									>
										Select the ASHLink Access Level:
									</Typography>
									<Typography>
										<Link
											component='button'
											type='button'
											onClick={() => {
												setShowPermissions(true);
											}}
											sx={{
												cursor: 'pointer',
												textDecoration: 'underline',
												border: 'none',
												background: 'none',
												padding: 0,
												font: 'inherit',
												my: 2,
											}}
										>
											View Permissions
										</Link>
									</Typography>
									<RadioGroup
										defaultValue={3}
										aria-labelledby='accessLevel-label'
										name='accessLevel'
										error={
											touched.accessLevel &&
											Boolean(errors.accessLevel)
										}
										onChange={e => {
											handleChange(e);
											setAshlinkUserLevel(
												Number(e.target.value)
											);
										}}
									>
										<FormControlLabel
											value={1}
											control={<Radio />}
											label={
												<Typography>
													<strong>Level 1: </strong>
													{getLevel1Text(
														providerUserTypeGroup
													)}
												</Typography>
											}
										/>
										<FormControlLabel
											value={2}
											control={<Radio />}
											label={
												<Typography>
													<strong>Level 2: </strong>
													{getLevel2Text(
														providerUserTypeGroup
													)}
												</Typography>
											}
										/>
										<FormControlLabel
											value={3}
											control={<Radio />}
											label={
												<Typography>
													<strong>Level 3: </strong>
													{getLevel3Text(
														providerUserTypeGroup
													)}
												</Typography>
											}
										/>
									</RadioGroup>
								</Box>

								<Box>
									<Typography
										sx={{
											fontStyle: 'italic',
											mt: 6,
											mb: 3.5,
										}}
									>
										If you have any questions, please select
										&lsquo;Help&lsquo; in the navigation and
										&lsquo;Submit an online Question or
										Comment&lsquo;. Responses can be viewed
										in your &lsquo;Inbox&lsquo;.
									</Typography>
								</Box>

								<Stack direction='row'>
									<LoadingButton
										type='submit'
										variant='contained'
										isLoading={isFetching}
										sx={{
											mr: 2,
										}}
									>
										Create new sub account
									</LoadingButton>
									<Button
										type='button'
										variant='outlined'
										onClick={() =>
											navigate(
												'/account/manageSubAccounts'
											)
										}
									>
										Cancel
									</Button>
								</Stack>
							</Stack>
						</Form>
					)}
				</Formik>
			</Paper>
			<Snackbar
				open={!!openSnackbar}
				autoHideDuration={10000}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				TransitionComponent={SlideLeft}
				sx={{
					boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
				}}
			>
				<Alert severity='error'>
					<Typography fontWeight={700}>{alertText.title}</Typography>
					<Typography>{alertText.description}</Typography>
				</Alert>
			</Snackbar>

			<PermissionsModal
				open={showPermissions}
				onClose={() => setShowPermissions(false)}
			/>
		</>
	);
};
export default CreateSubAccount;
